/** Tooltip when the activity is not under a Week but the restriction preference may still be stored. */
export const DISCUSSION_WEEK_RESTRICTION_UNAVAILABLE_TOOLTIP =
    'Setting only available and applied when activity is placed within a Week.';

/**
 * Whether the week-start participation restriction currently applies to students/TAs.
 * `restrict_until_week_start` may remain true when the activity is moved outside a Week, but
 * enforcement and banners must be off until it sits under a Week again.
 *
 * @param {object|null|undefined} activityContent - `activity_content` from API or editor store
 * @returns {boolean}
 */
export function isDiscussionWeekRestrictionActive(activityContent) {
    if (!activityContent || activityContent.restricted_until_week_start_active !== true) {
        return false;
    }
    if (activityContent.parent_week_available === false) {
        return false;
    }
    return true;
}

/**
 * Whether the Discussion Thread "restrict until week start" setting may be toggled.
 * When the activity exists in the editor store, derive placement from getWeekStatus (parent / section_id),
 * which updates as soon as the item is moved. parent_week_available from the API can stay stale until refetch.
 * If the activity is not in the store or week resolution throws, fall back to parent_week_available.
 *
 * @param {object} params
 * @param {object} params.store - Vuex store
 * @param {string} params.namespace - e.g. ActivityTemplates | Activities
 * @param {string} params.uuid - activity uuid in the store
 * @param {boolean} params.template - true when editing course templates (SectionTemplates), false for live courses (Sections)
 */
export function isDiscussionWeekRestrictionSettingAvailable({ store, namespace, uuid, template }) {
    const getItem = store.getters[`${namespace}/get`];
    const item = typeof getItem === 'function' ? getItem(uuid) : null;

    if (item) {
        try {
            const weekStatus = store.getters[`${namespace}/getWeekStatus`]({ uuid, template });
            if (weekStatus) {
                return weekStatus.type !== 'root-level';
            }
        } catch {
            // getWeekStatus / section walk can throw if parent sections are missing from the store
        }
    }

    const fromApi = store.getters[`${namespace}/activityContent`](uuid, 'parent_week_available');
    if (fromApi === true) {
        return true;
    }
    if (fromApi === false) {
        return false;
    }

    try {
        const weekStatus = store.getters[`${namespace}/getWeekStatus`]({ uuid, template });
        if (!weekStatus) {
            return false;
        }
        return weekStatus.type !== 'root-level';
    } catch {
        return false;
    }
}
